import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "./ui/button";
import { Truck, ShieldCheck, MapPin, Fuel } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1598179875484-1104757ef967?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxvaWwlMjB0YW5rZXIlMjB0cnVjayUyMHJlZmluZXJ5JTIwZnVlbCUyMHRyYW5zcG9ydCUyMGhpZ2h3YXl8ZW58MHx8fGJsdWV8MTc4OTc4NDkxNHww&ixlib=rb-4.1.0&q=85";

const STATS = [
  { icon: Truck, value: "10,000+", label: "Successful Deliveries" },
  { icon: MapPin, value: "36", label: "States Covered" },
  { icon: Fuel, value: "50+", label: "Tanker Fleet" },
  { icon: ShieldCheck, value: "100%", label: "Safety Commitment" },
];

const LINES = [
  { text: "Powering Nigeria\u2019s", amber: false },
  { text: "Energy Movement,", amber: false },
  { text: "Delivered with Dignity.", amber: true },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden grain-overlay" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img src={HERO_IMG} alt="Motaps Global petroleum tanker on the highway" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B132B]/85 via-[#0B132B]/65 to-[#0B132B]" />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="relative max-w-7xl mx-auto px-5 md:px-8 w-full pt-36 pb-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="text-amber-500 font-display font-semibold tracking-[0.4em] uppercase text-xs md:text-sm mb-8"
          data-testid="hero-eyebrow"
        >
          Petroleum Products &bull; Logistics &bull; Distribution
        </motion.p>

        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[1.04] text-white max-w-4xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.amber ? "text-gradient-amber" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-7 text-base md:text-lg text-slate-300 max-w-2xl"
          data-testid="hero-subtext"
        >
          Motaps Global Investment Limited is a leading oil &amp; gas company and a major transporter for top oil marketers across Nigeria — lifting petroleum products from depots and delivering them safely, swiftly and reliably to every destination. We also bring premium diesel straight to your doorstep.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-500 text-white rounded-full px-8 text-base transition-transform duration-200 hover:-translate-y-0.5">
            <a href="#order" data-testid="hero-order-diesel-btn">Order Door-to-Door Diesel</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-8 text-base border-slate-500 text-slate-100 bg-transparent hover:bg-white/10 hover:text-white transition-transform duration-200 hover:-translate-y-0.5">
            <a href="#services" data-testid="hero-services-btn">Explore Our Services</a>
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: EASE }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-700/50 rounded-2xl overflow-hidden border border-slate-700/50"
          data-testid="hero-stats"
        >
          {STATS.map(({ icon: Icon, value, label }) => (
            <div key={label} className="bg-[#0B132B]/80 backdrop-blur-md p-6 flex flex-col gap-2" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={20} className="text-amber-500" />
              <span className="font-mono-stat text-2xl md:text-3xl font-bold text-white">{value}</span>
              <span className="text-xs md:text-sm text-slate-400">{label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
